import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type {
  U3204ConversationSearchRow,
  U3204DisplayMessage,
  U3204InitLU,
  U3204MissionRef,
  U3204ModelRef,
  U3204StreamState,
} from './U3204Service';
import type { U3202ModelCapabilities } from '../u3202AiProvider/utils/U3202CapabilitiesTypes';
import { useU3204ModelCapabilitiesHandlers } from './hooks/useU3204ModelCapabilitiesHandlers';
import { useU3204AccessibleMissionsHandlers, type U3204AccessibleMissionOption } from './hooks/useU3204AccessibleMissionsHandlers';
import { u3204CanSendInActiveConversation } from './utils/u3204CanSendInActiveConversation';

export interface U3204ContextType {
  initLU: U3204InitLU;
  conversations: U3204ConversationSearchRow[];
  setConversations: React.Dispatch<React.SetStateAction<U3204ConversationSearchRow[]>>;
  archivedConversations: U3204ConversationSearchRow[];
  setArchivedConversations: React.Dispatch<React.SetStateAction<U3204ConversationSearchRow[]>>;
  archivesExpanded: boolean;
  setArchivesExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  activeConversationId: string | null;
  setActiveConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  activeMission: U3204MissionRef | null;
  setActiveMission: React.Dispatch<React.SetStateAction<U3204MissionRef | null>>;
  messages: U3204DisplayMessage[];
  setMessages: React.Dispatch<React.SetStateAction<U3204DisplayMessage[]>>;
  streamState: U3204StreamState;
  setStreamState: React.Dispatch<React.SetStateAction<U3204StreamState>>;
  selectedMissionId: string | null;
  setSelectedMissionId: React.Dispatch<React.SetStateAction<string | null>>;
  loadingConversations: boolean;
  setLoadingConversations: React.Dispatch<React.SetStateAction<boolean>>;
  loadingArchivedConversations: boolean;
  setLoadingArchivedConversations: React.Dispatch<React.SetStateAction<boolean>>;
  loadingMessages: boolean;
  setLoadingMessages: React.Dispatch<React.SetStateAction<boolean>>;
  isNewChat: boolean;
  setIsNewChat: React.Dispatch<React.SetStateAction<boolean>>;
  conversationFilterText: string;
  setConversationFilterText: React.Dispatch<React.SetStateAction<string>>;
  activeConversationTitle: string | null;
  setActiveConversationTitle: React.Dispatch<React.SetStateAction<string | null>>;
  activeDefaultModel: U3204ModelRef | null;
  modelCapabilities: U3202ModelCapabilities;
  allowedAttachmentMimeTypes: string[];
  canAttachFiles: boolean;
  accessibleMissions: U3204AccessibleMissionOption[];
  loadingAccessibleMissions: boolean;
  loadAccessibleMissions: () => Promise<U3204AccessibleMissionOption[]>;
  canSendInActiveConversation: boolean;
}

const U3204Context = createContext<U3204ContextType | undefined>(undefined);

interface U3204ContextProviderProps {
  initLU: U3204InitLU;
  children: React.ReactNode;
}

export const U3204ContextProvider: React.FC<U3204ContextProviderProps> = ({ initLU, children }) => {
  const [conversations, setConversations] = useState<U3204ConversationSearchRow[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<U3204ConversationSearchRow[]>([]);
  const [archivesExpanded, setArchivesExpanded] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeMission, setActiveMission] = useState<U3204MissionRef | null>(null);
  const [messages, setMessages] = useState<U3204DisplayMessage[]>([]);
  const [streamState, setStreamState] = useState<U3204StreamState>('idle');
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingArchivedConversations, setLoadingArchivedConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isNewChat, setIsNewChat] = useState(true);
  const [conversationFilterText, setConversationFilterText] = useState('');
  const [activeConversationTitle, setActiveConversationTitle] = useState<string | null>(null);

  const { missions: accessibleMissions, loading: loadingAccessibleMissions, loadAccessibleMissions } =
    useU3204AccessibleMissionsHandlers();

  useEffect(() => {
    void loadAccessibleMissions();
  }, [loadAccessibleMissions]);

  const accessibleMissionIds = useMemo(
    () => new Set(accessibleMissions.map((mission) => mission.id)),
    [accessibleMissions],
  );

  const canSendInActiveConversation = useMemo(
    () =>
      u3204CanSendInActiveConversation(
        initLU.hasUserGroup,
        activeConversationId,
        activeMission?.id,
        accessibleMissionIds,
      ),
    [initLU.hasUserGroup, activeConversationId, activeMission?.id, accessibleMissionIds],
  );

  const {
    defaultModel: activeDefaultModel,
    capabilities: modelCapabilities,
    allowedMimeTypes: allowedAttachmentMimeTypes,
    canAttachFiles,
  } = useU3204ModelCapabilitiesHandlers(activeMission, initLU.allowedMimeTypes);

  const value = useMemo(
    () => ({
      initLU,
      conversations,
      setConversations,
      archivedConversations,
      setArchivedConversations,
      archivesExpanded,
      setArchivesExpanded,
      activeConversationId,
      setActiveConversationId,
      activeMission,
      setActiveMission,
      messages,
      setMessages,
      streamState,
      setStreamState,
      selectedMissionId,
      setSelectedMissionId,
      loadingConversations,
      setLoadingConversations,
      loadingArchivedConversations,
      setLoadingArchivedConversations,
      loadingMessages,
      setLoadingMessages,
      isNewChat,
      setIsNewChat,
      conversationFilterText,
      setConversationFilterText,
      activeConversationTitle,
      setActiveConversationTitle,
      activeDefaultModel,
      modelCapabilities,
      allowedAttachmentMimeTypes,
      canAttachFiles,
      accessibleMissions,
      loadingAccessibleMissions,
      loadAccessibleMissions,
      canSendInActiveConversation,
    }),
    [
      initLU,
      conversations,
      archivedConversations,
      archivesExpanded,
      activeConversationId,
      activeMission,
      messages,
      streamState,
      selectedMissionId,
      loadingConversations,
      loadingArchivedConversations,
      loadingMessages,
      isNewChat,
      conversationFilterText,
      activeConversationTitle,
      activeDefaultModel,
      modelCapabilities,
      allowedAttachmentMimeTypes,
      canAttachFiles,
      accessibleMissions,
      loadingAccessibleMissions,
      loadAccessibleMissions,
      canSendInActiveConversation,
    ],
  );

  return <U3204Context.Provider value={value}>{children}</U3204Context.Provider>;
};

export function useU3204Context(): U3204ContextType {
  const ctx = useContext(U3204Context);
  if (!ctx) {
    throw new Error('useU3204Context must be used within U3204ContextProvider');
  }
  return ctx;
}
